import { AfterViewInit, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { BondTypeService } from './bond-type.service';
import { BondType, UpsertBondTypeRequest } from './bond-type.types';
import { CatalogCrudModalComponent } from '../shared/components/catalog-crud-modal';
import { CatalogDeleteConfirmModalComponent } from '../shared/components/catalog-delete-confirm-modal';
import { CatalogPageCardComponent } from '../shared/components/catalog-page-card';
import { CatalogTableToolbarComponent } from '../shared/components/catalog-table-toolbar';
import { CatalogDatatableController, DatatableViewStateSnapshot } from '../shared/catalog-datatable.controller';
import { CatalogModalController } from '../shared/catalog-modal.controller';

interface BondTypeFormValue {
  name: string;
  description: string;
}

type BondTypeFormControlName = keyof BondTypeFormValue;

@Component({
  selector: 'app-bond-type',
  imports: [
    ReactiveFormsModule,
    CatalogPageCardComponent,
    CatalogTableToolbarComponent,
    CatalogCrudModalComponent,
    CatalogDeleteConfirmModalComponent,
  ],
  templateUrl: './bond-type.html',
  styleUrl: './bond-type.css',
})
export class BondTypeComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly nameMaxLength = 30;
  private readonly descriptionMaxLength = 50;
  private readonly datatableId = 'kt_bond_type_datatable';
  private readonly createModalId = 'create_bond_type_modal';
  private readonly editModalId = 'edit_bond_type_modal';
  private readonly deleteConfirmModalId = 'delete_bond_type_confirm_modal';
  private readonly formBuilder = inject(FormBuilder);
  private readonly datatableController = new CatalogDatatableController(this.datatableId);
  private readonly modalController = new CatalogModalController();

  private createModalObserver?: MutationObserver;
  private editModalObserver?: MutationObserver;

  protected readonly createForm = this.formBuilder.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(this.nameMaxLength)]],
    description: ['', [Validators.maxLength(this.descriptionMaxLength)]],
  });
  protected readonly editForm = this.formBuilder.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(this.nameMaxLength)]],
    description: ['', [Validators.maxLength(this.descriptionMaxLength)]],
  });
  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal('');
  protected readonly rows = signal<BondType[]>([]);
  protected readonly isCreating = signal(false);
  protected readonly createFormSubmitted = signal(false);
  protected readonly createFormError = signal('');
  protected readonly editingId = signal<number | null>(null);
  protected readonly isUpdating = signal(false);
  protected readonly editFormSubmitted = signal(false);
  protected readonly editFormError = signal('');
  protected readonly deletingId = signal<number | null>(null);
  protected readonly pendingDeleteId = signal<number | null>(null);
  protected readonly alertVariant = signal<'success' | 'destructive' | null>(null);
  protected readonly alertTitle = signal('');
  protected readonly alertMessage = signal('');
  protected readonly hasAlert = computed(() => this.alertVariant() !== null);

  constructor(private readonly bondTypeService: BondTypeService) {}

  ngOnInit(): void {
    this.loadRows();
  }

  ngAfterViewInit(): void {
    this.bindCreateModalCloseReset();
    this.bindEditModalCloseReset();
  }

  ngOnDestroy(): void {
    this.createModalObserver?.disconnect();
    this.editModalObserver?.disconnect();
  }

  protected shouldShowCreateFieldError(controlName: BondTypeFormControlName): boolean {
    return this.createFormSubmitted() && this.createForm.controls[controlName].invalid;
  }

  protected shouldShowEditFieldError(controlName: BondTypeFormControlName): boolean {
    return this.editFormSubmitted() && this.editForm.controls[controlName].invalid;
  }

  protected shouldShowCreateRequiredError(controlName: BondTypeFormControlName): boolean {
    const control = this.createForm.controls[controlName];
    return this.createFormSubmitted() && !!control.errors?.['required'];
  }

  protected shouldShowCreateMaxLengthError(controlName: BondTypeFormControlName): boolean {
    const control = this.createForm.controls[controlName];
    return this.createFormSubmitted() && !!control.errors?.['maxlength'];
  }

  protected shouldShowEditRequiredError(controlName: BondTypeFormControlName): boolean {
    const control = this.editForm.controls[controlName];
    return this.editFormSubmitted() && !!control.errors?.['required'];
  }

  protected shouldShowEditMaxLengthError(controlName: BondTypeFormControlName): boolean {
    const control = this.editForm.controls[controlName];
    return this.editFormSubmitted() && !!control.errors?.['maxlength'];
  }

  protected loadRows(snapshot?: DatatableViewStateSnapshot): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.bondTypeService.getBondTypes().subscribe({
      next: (rows) => {
        this.rows.set(rows);
        this.isLoading.set(false);
        this.datatableController.initialize(snapshot);
      },
      error: () => {
        this.errorMessage.set('Unable to load bond type data from the API.');
        this.isLoading.set(false);
      },
    });
  }

  protected onCreate(): void {
    this.createFormSubmitted.set(true);
    this.createFormError.set('');

    if (this.createForm.invalid) {
      this.createForm.markAllAsTouched();
      return;
    }

    const payload = this.toPayload(this.createForm.getRawValue());

    this.isCreating.set(true);

    this.bondTypeService.createBondType(payload).subscribe({
      next: () => {
        this.isCreating.set(false);
        this.closeCreateModal();
        this.resetCreateForm();
        window.KTToast?.show({
          message: 'Bond type created successfully.',
          variant: 'success',
        });
        this.loadRows();
      },
      error: () => {
        this.isCreating.set(false);
        this.createFormError.set('Unable to create bond type at this time. Please try again.');
      },
    });
  }

  protected resetCreateForm(): void {
    this.createForm.reset(this.getEmptyFormValue());
    this.createFormSubmitted.set(false);
    this.createFormError.set('');
  }

  protected openEditModal(row: BondType): void {
    this.editingId.set(row.id);
    this.editForm.reset({
      name: row.name,
      description: row.description,
    });
    this.editFormSubmitted.set(false);
    this.editFormError.set('');
    this.modalController.open(this.editModalId);
  }

  protected onUpdate(): void {
    this.editFormSubmitted.set(true);
    this.editFormError.set('');

    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }

    const id = this.editingId();
    if (id === null) {
      this.editFormError.set('Unable to identify the bond type to update.');
      return;
    }

    const payload = this.toPayload(this.editForm.getRawValue());
    const datatableSnapshot = this.datatableController.captureViewState();

    this.isUpdating.set(true);

    this.bondTypeService.updateBondType(id, payload).subscribe({
      next: () => {
        this.isUpdating.set(false);
        this.closeEditModal();
        this.resetEditForm();
        window.KTToast?.show({
          message: 'Bond type updated successfully.',
          variant: 'success',
        });
        this.loadRows(datatableSnapshot);
      },
      error: () => {
        this.isUpdating.set(false);
        this.editFormError.set('Unable to update bond type at this time. Please try again.');
      },
    });
  }

  protected resetEditForm(): void {
    this.editForm.reset(this.getEmptyFormValue());
    this.editingId.set(null);
    this.editFormSubmitted.set(false);
    this.editFormError.set('');
  }

  protected onTableActionClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    const editButton = target?.closest('button[data-action="edit-bond-type"]') as HTMLButtonElement | null;
    const deleteButton = target?.closest('button[data-action="delete-bond-type"]') as HTMLButtonElement | null;

    if (editButton && !editButton.disabled) {
      const rowId = this.getRowIdFromButton(editButton);
      if (rowId === null) {
        return;
      }

      const row = this.rows().find((item) => item.id === rowId);
      if (!row) {
        return;
      }

      this.openEditModal(row);
      return;
    }

    if (!deleteButton || deleteButton.disabled) {
      return;
    }

    const rowId = this.getRowIdFromButton(deleteButton);
    if (rowId === null) {
      return;
    }

    this.openDeleteConfirmationModal(rowId);
  }

  protected cancelDelete(): void {
    this.pendingDeleteId.set(null);
    this.closeDeleteConfirmationModal();
  }

  protected confirmDelete(): void {
    const rowId = this.pendingDeleteId();

    if (rowId === null || this.deletingId() !== null) {
      return;
    }

    this.closeDeleteConfirmationModal();
    this.pendingDeleteId.set(null);
    this.removeRow(rowId);
  }

  protected removeRow(rowId: number): void {
    if (this.deletingId() !== null) {
      return;
    }

    this.deletingId.set(rowId);

    this.bondTypeService.deleteBondType(rowId).subscribe({
      next: (result) => {
        this.deletingId.set(null);

        if (result.kind === 'success') {
          const snapshot = this.datatableController.captureViewState();
          this.rows.update((rows) => rows.filter((item) => item.id !== rowId));
          this.showSuccessAlert('Bond type deleted successfully.');
          this.datatableController.initialize(snapshot);
          return;
        }

        this.showDestructiveAlert(
          'Unable to delete bond type.',
          result.message,
        );
      },
      error: () => {
        this.deletingId.set(null);
        this.showDestructiveAlert(
          'Unable to delete bond type.',
          'Unable to delete bond type at this time. Please try again.',
        );
      },
    });
  }

  protected dismissAlert(): void {
    this.alertVariant.set(null);
    this.alertTitle.set('');
    this.alertMessage.set('');
  }

  private bindCreateModalCloseReset(): void {
    this.createModalObserver = this.modalController.observeClose(this.createModalId, () => {
      this.resetCreateForm();
    });
  }

  private bindEditModalCloseReset(): void {
    this.editModalObserver = this.modalController.observeClose(this.editModalId, () => {
      this.resetEditForm();
    });
  }

  private closeCreateModal(): void {
    this.modalController.close(this.createModalId);
  }

  private closeEditModal(): void {
    this.modalController.close(this.editModalId);
  }

  private openDeleteConfirmationModal(rowId: number): void {
    this.pendingDeleteId.set(rowId);
    this.modalController.open(this.deleteConfirmModalId);
  }

  private closeDeleteConfirmationModal(): void {
    this.modalController.close(this.deleteConfirmModalId);
  }

  private showSuccessAlert(message: string): void {
    this.alertVariant.set('success');
    this.alertTitle.set(message);
    this.alertMessage.set('');
  }

  private showDestructiveAlert(title: string, message: string): void {
    this.alertVariant.set('destructive');
    this.alertTitle.set(title);
    this.alertMessage.set(message);
  }

  private getRowIdFromButton(button: HTMLButtonElement): number | null {
    const rowIdRaw = button.getAttribute('data-bond-type-id');
    const rowId = rowIdRaw ? Number(rowIdRaw) : Number.NaN;

    return Number.isFinite(rowId) ? rowId : null;
  }

  private getEmptyFormValue(): BondTypeFormValue {
    return {
      name: '',
      description: '',
    };
  }

  private toPayload(rawValue: BondTypeFormValue): UpsertBondTypeRequest {
    const description = rawValue.description.trim();

    return {
      name: rawValue.name.trim(),
      description: description ? description : null,
    };
  }
}
